import React from 'react';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  ListItemButton,
  useTheme,
  Divider,
  Avatar,
  Collapse,
  Menu,
  MenuItem,
  ListItemAvatar,
  Tooltip,
  Breadcrumbs,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Folder as CasesIcon,
  People as ClientsIcon,
  Description as DocumentsIcon,
  Event as CalendarIcon,
  Settings as SettingsIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Receipt as BillingIcon,
  ReceiptLong as ProformaIcon,
  RequestQuote as BillIcon,
  TrendingUp as RevenuesIcon,
  Group as UsersIcon,
  ContactPhone as ContactsIcon,
  Chat as ChatIcon,
  CheckCircle as TasksIcon,
  RateReview as ReviewIcon,
  Assessment as ReportsIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  Business as FirmsIcon,
  Person as PersonIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  History as AuditLogIcon,
} from '@mui/icons-material';
import { RootState } from '../store';
import { logout } from '../store/slices/authSlice';
import { ONIN_LOGO_LIGHT, ONIN_LOGO_DARK } from '../components/common/BrandLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import NotificationsMenu from '../components/common/NotificationsMenu';
import { formatVersion } from '../utils/version';
import { useThemeMode } from '../contexts/ThemeModeContext';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;
const mobileDrawerWidth = 280;

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

const getSidebarColors = (mode: string) => ({
  background: mode === 'light' ? '#f8fafc' : '#0f172a',
  border: mode === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)',
  textPrimary: mode === 'light' ? '#0f172a' : '#f1f5f9',
  textSecondary: mode === 'light' ? '#64748b' : '#94a3b8',
  hover: mode === 'light' ? 'rgba(15, 23, 42, 0.06)' : 'rgba(255, 255, 255, 0.06)',
  selectedBg: mode === 'light' ? 'rgba(59, 130, 246, 0.16)' : 'rgba(96, 165, 250, 0.16)',
  selectedBgHover: mode === 'light' ? 'rgba(59, 130, 246, 0.24)' : 'rgba(96, 165, 250, 0.24)',
  selectedText: mode === 'light' ? '#2563eb' : '#60a5fa',
});

interface MenuItemType {
  text: string;
  icon: React.ReactNode;
  path?: string;
  children?: { text: string; icon: React.ReactNode; path: string }[];
}

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [billingOpen, setBillingOpen] = React.useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = React.useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { mode, toggleColorMode } = useThemeMode();
  const sidebar = getSidebarColors(mode);
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { organization } = useSelector((state: RootState) => state.organization);

  // Role-based menu items
  const getRoleBasedMenuItems = (): MenuItemType[] => {
    // Super Admin manages the platform itself (tenants/firms, users,
    // system-wide reports, audit log) — not any one firm's case work, so
    // it gets its own menu rather than sharing Admin's (Phase 8).
    if (user?.role?.name === 'super-admin') {
      return [
        { text: t('navigation.dashboard'), icon: <DashboardIcon />, path: '/' },
        { text: t('navigation.firms'), icon: <FirmsIcon />, path: '/firms' },
        { text: t('navigation.users'), icon: <UsersIcon />, path: '/users' },
        { text: t('navigation.reports'), icon: <ReportsIcon />, path: '/reports' },
        { text: t('navigation.auditLog'), icon: <AuditLogIcon />, path: '/audit-log' },
        { text: t('navigation.settings'), icon: <SettingsIcon />, path: '/settings' },
      ];
    }

    // Admin - full firm-scoped access
    if (user?.role?.name === 'admin') {
      return [
        { text: t('navigation.dashboard'), icon: <DashboardIcon />, path: '/' },
        { text: t('navigation.cases'), icon: <CasesIcon />, path: '/cases' },
        { text: t('navigation.clients'), icon: <ClientsIcon />, path: '/clients' },
        { text: t('navigation.documents'), icon: <DocumentsIcon />, path: '/documents' },
        { text: t('navigation.calendar'), icon: <CalendarIcon />, path: '/calendar' },
        {
          text: t('navigation.billing'),
          icon: <BillingIcon />,
          children: [
            { text: t('navigation.proforma'), icon: <ProformaIcon />, path: '/billing/proforma' },
            { text: t('navigation.bills'), icon: <BillIcon />, path: '/billing/bills' },
            { text: t('navigation.revenues'), icon: <RevenuesIcon />, path: '/billing/revenues' },
          ],
        },
        { text: t('navigation.contacts'), icon: <ContactsIcon />, path: '/contacts' },
        { text: t('navigation.chat'), icon: <ChatIcon />, path: '/chat' },
        { text: t('navigation.tasks'), icon: <TasksIcon />, path: '/tasks' },
        { text: t('navigation.customersReview'), icon: <ReviewIcon />, path: '/customers-review' },
        { text: t('navigation.settings'), icon: <SettingsIcon />, path: '/settings' },
        { text: t('navigation.users'), icon: <UsersIcon />, path: '/users' },
        { text: t('navigation.reports'), icon: <ReportsIcon />, path: '/reports' },
      ];
    }

    // Lawyer, Staff, and all other roles - limited menu items
    const limitedItems: MenuItemType[] = [
      { text: t('navigation.cases'), icon: <CasesIcon />, path: '/cases' },
      { text: t('navigation.chat'), icon: <ChatIcon />, path: '/chat' },
      { text: t('navigation.tasks'), icon: <TasksIcon />, path: '/tasks' },
      { text: t('navigation.contacts'), icon: <ContactsIcon />, path: '/contacts' },
      { text: t('navigation.calendar'), icon: <CalendarIcon />, path: '/calendar' },
      { text: t('navigation.documents'), icon: <DocumentsIcon />, path: '/documents' },
      { text: t('navigation.clients'), icon: <ClientsIcon />, path: '/clients' },
    ];

    return limitedItems;
  };

  const menuItems = getRoleBasedMenuItems();

  const isBillingActive = location.pathname.startsWith('/billing');

  React.useEffect(() => {
    if (isBillingActive) {
      setBillingOpen(true);
    }
  }, [isBillingActive]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopCollapseToggle = () => {
    setDesktopCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  const currentDrawerWidth = desktopCollapsed ? collapsedDrawerWidth : drawerWidth;

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    try {
      await dispatch(logout() as any);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Force redirect even if logout fails
      navigate('/login');
    }
  };

  const getDisplayName = () => {
    if (user?.full_name) {
      return user.full_name;
    }
    return user?.email || 'User';
  };

  const getRoleDisplay = () => {
    const roleMap: Record<string, string> = {
      'super-admin': t('users.superAdmin'),
      admin: t('users.admin'),
      lawyer: t('users.lawyer'),
      client: t('users.client'),
      staff: t('users.staff'),
    };
    return roleMap[user?.role.name || ''] || 'User';
  };

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map((name) => name[0]).join('').toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const getPageTitle = (): string => {
    for (const item of menuItems) {
      if (item.path && item.path === location.pathname) return item.text;
      if (item.children) {
        const child = item.children.find((c) => c.path === location.pathname);
        if (child) return `${item.text} - ${child.text}`;
      }
    }
    return t('navigation.dashboard');
  };

  const getBreadcrumbs = (): string[] => {
    const dashboardLabel = t('navigation.dashboard');
    if (location.pathname === '/') {
      return [dashboardLabel];
    }
    const crumbs = [dashboardLabel];
    // Nested detail pages (e.g. a case detail) get an extra crumb for their parent section
    if (location.pathname.startsWith('/cases/') && location.pathname !== '/cases') {
      crumbs.push(t('navigation.cases'));
    }
    crumbs.push(getPageTitle());
    return crumbs;
  };

  const renderDrawerContent = (collapsed: boolean, showCollapseToggle: boolean) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          display: 'flex',
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: collapsed ? 1 : 1,
          borderBottom: `1px solid ${sidebar.border}`,
          backgroundColor: sidebar.background,
          minHeight: 64,
        }}
      >
        {/* Logo shrinks to an icon-sized mark when collapsed instead of
            disappearing entirely — previously this whole block was hidden
            (`{!collapsed && ...}`), leaving nothing but the toggle button.
            The wrapper caps the logo's width and clips overflow so an
            oversized firm logo can't grow past the toggle button and hide
            it — a fixed-height image with a very wide aspect ratio would
            otherwise render wider than the drawer and push/cover it. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          {user?.firm?.logo_url ? (
            <Box
              component="img"
              src={user?.firm?.logo_url}
              alt={`${user?.firm?.name} Logo`}
              sx={{
                height: collapsed ? 32 : '110px',
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
              onError={(e) => {
                // Fallback to default logo if organization logo fails to load
                (e.target as HTMLImageElement).src = mode === 'dark' ? ONIN_LOGO_DARK : ONIN_LOGO_LIGHT;
              }}
            />
          ) : (
            <Box
              component="img"
              // White mark reads on the dark-mode sidebar background;
              // the colored one reads on the light-mode one.
              src={mode === 'dark' ? ONIN_LOGO_DARK : ONIN_LOGO_LIGHT}
              alt="ONIN Legal Case Manager"
              sx={{
                height: collapsed ? 28 : 45,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>
        {showCollapseToggle && (
          <Tooltip title={collapsed ? t('navigation.expandSidebar', 'Expand sidebar') : t('navigation.collapseSidebar', 'Collapse sidebar')}>
            <IconButton
              onClick={handleDesktopCollapseToggle}
              size="small"
              sx={{ color: sidebar.textSecondary, flexShrink: 0 }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {!collapsed && (
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ color: sidebar.textSecondary }}>
            {t('navigation.legalCaseManagement')}
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: sidebar.border }} />

      <List sx={{ flex: 1, px: collapsed ? 1 : 2, overflow: 'auto' }}>
        {menuItems.map((item) => {
          if (item.children) {
            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip title={collapsed ? item.text : ''} placement="right">
                    <ListItemButton
                      onClick={() => setBillingOpen(!billingOpen)}
                      sx={{
                        borderRadius: 1,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        px: collapsed ? 1 : 2,
                        color: sidebar.textPrimary,
                        ...(isBillingActive && {
                          backgroundColor: sidebar.selectedBg,
                          color: sidebar.selectedText,
                          '& .MuiListItemIcon-root': {
                            color: sidebar.selectedText,
                          },
                        }),
                        '&:hover': {
                          backgroundColor: isBillingActive
                            ? sidebar.selectedBgHover
                            : sidebar.hover,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed ? 0 : 40,
                          mr: collapsed ? 0 : undefined,
                          justifyContent: 'center',
                          color: isBillingActive
                            ? sidebar.selectedText
                            : sidebar.textSecondary
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: isBillingActive ? 600 : 400,
                          }}
                        />
                      )}
                      {!collapsed && (billingOpen ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
                <Collapse in={billingOpen && !collapsed} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.children.map((child) => (
                      <ListItem key={child.text} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => navigate(child.path)}
                          selected={location.pathname === child.path}
                          sx={{
                            borderRadius: 1,
                            pl: 4,
                            color: sidebar.textPrimary,
                            '&.Mui-selected': {
                              backgroundColor: sidebar.selectedBg,
                              color: sidebar.selectedText,
                              '&:hover': {
                                backgroundColor: sidebar.selectedBgHover,
                              },
                              '& .MuiListItemIcon-root': {
                                color: sidebar.selectedText,
                              },
                            },
                            '&:hover': {
                              backgroundColor: sidebar.hover,
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: location.pathname === child.path
                                ? sidebar.selectedText
                                : sidebar.textSecondary
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.text}
                            primaryTypographyProps={{
                              fontSize: '0.8rem',
                              fontWeight: location.pathname === child.path ? 600 : 400,
                            }}
                          />
                          {location.pathname === child.path && (
                            <ChevronRightIcon
                              sx={{
                                color: sidebar.selectedText,
                                opacity: 0.8,
                                fontSize: '1.2rem',
                              }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={collapsed ? item.text : ''} placement="right">
                <ListItemButton
                  onClick={() => navigate(item.path!)}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: 1,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 1 : 2,
                    color: sidebar.textPrimary,
                    '&.Mui-selected': {
                      backgroundColor: sidebar.selectedBg,
                      color: sidebar.selectedText,
                      '&:hover': {
                        backgroundColor: sidebar.selectedBgHover,
                      },
                      '& .MuiListItemIcon-root': {
                        color: sidebar.selectedText,
                      },
                    },
                    '&:hover': {
                      backgroundColor: sidebar.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 40,
                      justifyContent: 'center',
                      color: location.pathname === item.path
                        ? sidebar.selectedText
                        : sidebar.textSecondary
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 600 : 400,
                      }}
                    />
                  )}
                  {!collapsed && location.pathname === item.path && (
                    <ChevronRightIcon
                      sx={{
                        color: sidebar.selectedText,
                        opacity: 0.8,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: sidebar.border }} />

      <Box sx={{ p: collapsed ? 1 : 2, borderTop: `1px solid ${sidebar.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: collapsed ? 'center' : 'flex-start' }}>
          <Tooltip title={collapsed ? getDisplayName() : 'Click for profile options'}>
            <Box
              onClick={handleProfileMenuOpen}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                width: collapsed ? 'auto' : '100%',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
                src={user?.avatar_url}
              >
                {user?.avatar_url ? '' : getInitials()}
              </Avatar>
              {!collapsed && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                      fontWeight: 600,
                      color: sidebar.textPrimary
                    }}
                  >
                    {getDisplayName()}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: sidebar.textSecondary }}
                  >
                    {getRoleDisplay()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 200,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {t('common.profile')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('common.logout')}
        </MenuItem>
      </Menu>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: {
            xs: '100%',
            sm: `calc(100% - ${currentDrawerWidth}px)`
          },
          ml: {
            xs: 0,
            sm: `${currentDrawerWidth}px`
          },
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create(['width', 'margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={{ 
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 1, sm: 2, lg: 3 }
        }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' }, 
              color: 'text.primary',
              width: 40,
              height: 40
            }}
          >
            <MenuIcon />
          </IconButton>
          <Breadcrumbs
            separator="/"
            sx={{
              flexGrow: 1,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              '& .MuiBreadcrumbs-separator': { color: 'text.disabled', mx: 0.75 },
            }}
          >
            {getBreadcrumbs().map((crumb, i, arr) => (
              <Typography
                key={`${crumb}-${i}`}
                variant="body2"
                noWrap
                color={i === arr.length - 1 ? 'text.primary' : 'text.secondary'}
                sx={{ fontWeight: i === arr.length - 1 ? 600 : 400 }}
              >
                {crumb}
              </Typography>
            ))}
          </Breadcrumbs>
          <NotificationsMenu />
          <Tooltip title={mode === 'dark' ? t('navigation.lightMode', 'Switch to light mode') : t('navigation.darkMode', 'Switch to dark mode')}>
            <IconButton onClick={toggleColorMode} sx={{ color: 'text.primary', mr: 1 }}>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          <LanguageSwitcher />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: mobileDrawerWidth,
              backgroundColor: sidebar.background,
            },
          }}
        >
          {renderDrawerContent(false, false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              backgroundColor: sidebar.background,
              borderRight: `1px solid ${sidebar.border}`,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {renderDrawerContent(desktopCollapsed, true)}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0, // prevent wide children (e.g. DataGrid) from stretching this flex item and pushing content under the drawer
          pb: 8, // Extra bottom padding to account for fixed footer
          ml: { sm: `${currentDrawerWidth}px` }, // Left margin to account for drawer
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          transition: theme.transitions.create('margin-left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={{ mb: { xs: 1, sm: 2 } }} />
        <Box
          sx={{
            p: { xs: 2, sm: 3, lg: 4 },
            pt: 0,
            maxWidth: { xl: '1600px' },
            mx: { xl: 'auto' },
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Fixed Footer */}
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, sm: `${currentDrawerWidth}px` },
          right: 0,
          transition: theme.transitions.create('left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          height: '40px',
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          zIndex: theme.zIndex.appBar - 1,
        }}
      >
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ fontSize: '0.75rem' }}
        >
          All rights reserved © Tanga Africa - {formatVersion()}
        </Typography>
      </Box>
    </Box>
  );
};

export default MainLayout;
